import { AxiosResponse } from "axios";
import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { useMutation, useQuery, useQueryClient } from "react-query";
import Select from "react-select";
import { toast } from "react-toastify";
import RequisitionType from "Services/Requisition/RequisitionTypeService";
import useLang from "Shared/hooks/useLanguage";
import { reactSelectStyle, styles } from "Utils/Common";

function RejectModal({
  show,
  setShow,
  selectedRecord,
  loadGridData,
  setSelectedBox,
}: {
  setShow: any;
  show: boolean;
  selectedRecord: number[];
  loadGridData: (loader: boolean, reset: boolean, sortingState?: any) => void;
  setSelectedBox: any;
}) {
  const { t } = useLang();

  const initialAddRejection = {
    subject: "",
    description: "",
  };

  const [reason, setReason] = useState("");
  const [inputFields, setInputFields] = useState<boolean>(false);
  const [selectedReason, setSelectedReason] = useState<any>(null);
  const [addRejection, setAddRejection] = useState<{
    subject: string;
    description: string;
  }>(initialAddRejection);

  const queryClient = useQueryClient();

  const ModalhandleClose = () => setShow(false);

  const { data: rejectionReasonsData = [] } = useQuery(
    ["rejection-reasons"],
    async () => {
      const res = await RequisitionType.GetRejectionReasonLookup();
      return res?.data;
    },
    {
      enabled: show, // 🔥 runs ONLY when modal is open
    }
  );

  const useRejectDigitalCheckIn = () => {
    return useMutation({
      mutationFn: async (obj: any) => {
        const res: AxiosResponse =
          await RequisitionType.RejectDigitalCheckIn(obj);
        return res;
      },
      onSuccess: (res) => {
        if (res?.data?.httpStatusCode === 200) {
          toast.success(t("Record Successfully Processed"));
          setSelectedReason(null);
          setShow(false);
          setSelectedBox({ ids: [] });
          loadGridData(true, false);
        } else {
          toast.error(t("Error ..."));
          setSelectedReason(null);
        }
      },
      onError: () => {
        toast.error(t("An error occurred while processing the request."));
      },
    });
  };

  const handleAddReasonClick = () => {
    setInputFields(true); // Show the input fields on button click
  };

  const handleChangeRejectionReasonDropdown = (event: any) => {
    setSelectedReason(event.value);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setAddRejection((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddReasonClickClose = () => {
    setInputFields(false);
    setAddRejection(initialAddRejection); // Show the input fields on button click
  };

  const useAddRejectionReason = () => {
    return useMutation({
      mutationFn: async (obj: any) => {
        const res = await RequisitionType.AddRejectionReason(obj);
        return res;
      },
      onSuccess: (res: any) => {
        if (res.status === 200) {
          toast.success(t(res?.data?.message));
          handleAddReasonClickClose();
          queryClient.invalidateQueries(["rejection-reasons"]);
        }
      },
      onError: (err: any) => {
        console.error(err);
      },
    });
  };

  const RejectDigitalCheckIn = () => {
    if (!selectedReason) {
      return toast.error(
        t("A rejection reason is required. Please select one to proceed.")
      );
    }

    const obj = {
      statusId: 4,
      requisitionOrderIds: selectedRecord,
      actionReasons: selectedReason,
      rejectComment: reason,
    };

    rejectDigitalCheckInMutation(obj);
  };

  const addRejectionReason = () => {
    const obj = {
      text: addRejection?.subject,
      subject: addRejection?.subject,
      description: addRejection?.description,
      requisitionAssignment: [],
    };

    if (addRejection?.subject !== "" && addRejection?.description !== "") {
      addRejectionReasonMutation(obj);
    } else {
      toast.error(t("Please fill all the required fields."));
    }
  };

  const handleChangeForActionReason = (message: any) => {
    setReason(message);
  };

  const { mutate: addRejectionReasonMutation } = useAddRejectionReason();
  const { mutate: rejectDigitalCheckInMutation } = useRejectDigitalCheckIn();

  return (
    <Modal
      show={show}
      onHide={ModalhandleClose}
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton className="bg-light-primary m-0 p-5">
        <h4>{t("Reason")}</h4>
      </Modal.Header>
      <Modal.Body>
        <div className="fv-row mb-4">
          <label htmlFor="status" className="mb-2 required form-label">
            {t("Select Reason")}
          </label>
          <Select
            inputId="RejectModalReason"
            options={rejectionReasonsData}
            placeholder={"Select Reason"}
            theme={(theme) => styles(theme)}
            isSearchable={true}
            onChange={(e) => handleChangeRejectionReasonDropdown(e)}
            styles={reactSelectStyle}
          />
        </div>
        <div className="d-flex justify-content-end gap-2">
          <button
            id="RejectModalAddAnotherReason"
            className="btn btn-warning btn-sm mb-2"
            onClick={handleAddReasonClick}
          >
            {t("Add Another Reason")}
          </button>
        </div>
        {inputFields && (
          <>
            <div>
              <div className="mb-3">
                <label className="form-label required">{t("Subject")}</label>
                <input
                  id="RejectModalSubject"
                  type="text"
                  name="subject"
                  className="form-control bg-transparent"
                  placeholder={t("Subject")}
                  onChange={handleInputChange}
                />
              </div>
              <div className="mb-3">
                <label className="form-label required">
                  {t("Description")}
                </label>
                <input
                  id="RejectModalDescription"
                  type="text"
                  name="description"
                  className="form-control bg-transparent"
                  placeholder={t("Description")}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            <div className="d-flex justify-content-end gap-2">
              <button
                id="rejectModalCancel1st"
                className="btn btn-info btn-sm mb-2"
                onClick={handleAddReasonClickClose}
              >
                {t("Cancel")}
              </button>
              <button
                id="RejectModalSave1St"
                className="btn btn-primary btn-sm mb-2"
                onClick={addRejectionReason}
              >
                {t("Save")}
              </button>
            </div>
          </>
        )}
        <>
          <div>
            {selectedReason && (
              <div className="mt-5">
                <span className="fw-bold">{t("Reason")} : </span>
                <span className="text-danger">{selectedReason}</span>
              </div>
            )}
          </div>
          <div className="mt-5">
            <label className="form-label">{t("Rejection Comment")}</label>
            <textarea
              id="RejectModalRejectionComments"
              name="rejectComment"
              className="form-control bg-transparent mb-3 mb-lg-0 h-50px"
              placeholder={t("Rejection Comments")}
              required
              onChange={(event: any) =>
                handleChangeForActionReason(event?.target?.value)
              }
            />
          </div>
        </>
      </Modal.Body>
      <Modal.Footer className="p-0">
        <button
          id="RejectModalCancel"
          type="button"
          className="btn btn-secondary"
          onClick={ModalhandleClose}
        >
          {t("Cancel")}
        </button>
        <button
          id="RejectModalCancel"
          type="button"
          className="btn btn-danger m-2"
          onClick={() => {
            RejectDigitalCheckIn();
          }}
        >
          {t("Reject")}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

export default RejectModal;
